// File: frontend/src/hooks/useSystemChannelTopics.ts

import { useState, useCallback, useEffect, useRef } from "react";
import { CustomTopicModel } from "../models/CustomTopicModel";
import { customTopicService } from "../services/customTopicService";

const TOPICS_PAGE_SIZE = 20;

export type SystemChannelTopicsSortOption = "lastEntryAt" | "recentEntryCount";

/**
 * Data source for the İstikram and Bırak domain tabs — topics whose PRIMARY
 * channel (position 0) is that reserved system channel. Deliberately a
 * near-duplicate of useCustomTopics rather than a generalized version of it:
 * this hook has NO channel ("kanal") filter (İstikram/Bırak don't have one),
 * so keeping it separate avoids threading an unused channelFilter param
 * through Gündem's hook just for these two callers.
 *
 * @param systemKey         "istikram" | "birak" — which tab this instance backs.
 * @param autoLoad           Load the first page on mount.
 * @param showRookieEntries  Viewer preference, forwarded to the API — same
 *        semantics as useCustomTopics.
 */
export const useSystemChannelTopics = (
  systemKey: "istikram" | "birak",
  autoLoad: boolean = false,
  showRookieEntries: boolean = true
) => {
  const [topics, setTopics]           = useState<CustomTopicModel[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sortBy, setSortBy]           = useState<SystemChannelTopicsSortOption>("recentEntryCount");
  const showRookieRef = useRef(showRookieEntries);
  showRookieRef.current = showRookieEntries;

  /**
   * Core loader. Pass `append=true` to add the next page to the list.
   * Pass `silent=true` to skip the loading flag so the existing list
   * stays visible during refresh (matches pane Yenile behaviour).
   */
  const loadTopics = useCallback(async (
    sort: SystemChannelTopicsSortOption = "recentEntryCount",
    page = 0,
    append = false,
    silent = false
  ) => {
    if (append) {
      setIsLoadingMore(true);
    } else if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const sortDir = "desc"; // both sorts are descending (most recent / most active first)
      const result = await customTopicService.getSystemChannelTopics(
        systemKey, page, TOPICS_PAGE_SIZE, sort, sortDir, showRookieRef.current);
      if (append) {
        setTopics(prev => [...prev, ...result.content]);
      } else {
        setTopics(result.content);
      }
      setCurrentPage(page);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
    } catch (err) {
      console.error(`[useSystemChannelTopics:${systemKey}] loadTopics error:`, err);
      setError("Başlıklar yüklenemedi.");
    } finally {
      if (!silent) setLoading(false);
      setIsLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemKey]);

  // Load on mount — skipped when autoLoad is false so the parent can lazy-load
  // this pane only when the user first switches to it (App.tsx does exactly
  // that via loadedPanesRef, same as the other four domains).
  useEffect(() => {
    if (autoLoad) loadTopics("recentEntryCount", 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  // Reload when the viewer flips the "show rookie entries" preference, so the
  // badge counts refresh without requiring a manual Yenile.
  const isFirstPrefRun = useRef(true);
  useEffect(() => {
    if (isFirstPrefRun.current) {
      isFirstPrefRun.current = false;
      return;
    }
    loadTopics(sortBy, 0, false, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRookieEntries]);

  const loadMoreTopics = useCallback(async () => {
    const nextPage = currentPage + 1;
    if (isLoadingMore || nextPage >= totalPages) return;
    await loadTopics(sortBy, nextPage, true, false);
  }, [currentPage, totalPages, isLoadingMore, loadTopics, sortBy]);

  /**
   * Silent refresh — fetches fresh data without triggering the loading state,
   * so the list stays visible during the request (matches pane behaviour).
   */
  const refreshTopics = useCallback(async () => {
    await loadTopics(sortBy, 0, false, true);
  }, [loadTopics, sortBy]);

  /**
   * Shuffle ("Rastgele") — same one-shot random view as the Gündem hook, but
   * scoped to this tab's reserved system channel. Silent by design and resets
   * pagination to a single page.
   */
  const shuffleTopics = useCallback(async () => {
    setError(null);
    try {
      const result = await customTopicService.getRandomSystemChannelTopics(
        systemKey, TOPICS_PAGE_SIZE, showRookieRef.current);
      setTopics(result.content);
      setCurrentPage(0);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
    } catch (err) {
      console.error(`[useSystemChannelTopics:${systemKey}] shuffleTopics error:`, err);
      setError("Başlıklar yüklenemedi.");
    }
  }, [systemKey]);

  const handleSortChange = useCallback((newSort: SystemChannelTopicsSortOption) => {
    setSortBy(newSort);
    loadTopics(newSort, 0, false, false);
  }, [loadTopics]);

  return {
    topics,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    isLoadingMore,
    sortBy,
    loadTopics,
    loadMoreTopics,
    refreshTopics,
    shuffleTopics,
    handleSortChange,
  };
};